/** Single-line text input with label, description, and error. Wraps React Aria TextField. */
import {
  TextField as AriaTextField,
  Input,
  Label,
  Text,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/foundation/utils/cn';

const inputVariants = cva(
  [
    'w-full border border-[var(--f-border-strong)] rounded-xl box-border',
    'bg-[var(--f-bg-raised)] text-[var(--f-fg)]',
    'focus:outline-none focus:ring-2 focus:ring-[var(--f-accent)] focus:border-transparent',
    'transition-all',
  ],
  {
    variants: {
      size: {
        sm: 'px-2 py-1 text-sm h-8',
        md: 'px-3 py-2 text-sm h-10',
        lg: 'px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

interface TextFieldProps extends AriaTextFieldProps, VariantProps<typeof inputVariants> {
  label?: string;
  description?: string;
  errorMessage?: string;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  size,
  className,
  inputClassName,
  placeholder,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField className={cn('flex flex-col gap-1', className)} {...props}>
      {label && (
        <Label className="block text-sm font-medium text-[var(--f-fg-secondary)]">{label}</Label>
      )}
      <Input className={cn(inputVariants({ size }), inputClassName)} placeholder={placeholder} />
      {description && (
        <Text slot="description" className="text-xs text-[var(--f-fg-muted)]">
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text slot="errorMessage" className="text-xs text-[var(--f-error-text)]">
          {errorMessage}
        </Text>
      )}
    </AriaTextField>
  );
}
